import { App, Avatar, Button, Progress, Spin, Tooltip } from 'antd';
import clsx from 'clsx';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAvatarColor } from '../../../utils/avatarColors';
import type { CsvUserImportResultItem } from '../../../utils/csvUserImport';
import { useAppStore } from '../../../store/store';
import { Icon } from '../../Core/Icon';
import TruncatedTooltip from '../TruncatedTooltip';

/** Neutral avatar bg until create returns a userId (then getAvatarColor). */
const PENDING_AVATAR_BG = 'var(--color-gray-medium)';

/**
 * UserImportProgressDrawer
 *
 * Floating drawer for CSV user import — same chrome/row layout as UploadProgressDrawer.
 * Flat list: one row per user (pending / running / created / skipped / failed / cancelled).
 */
const UserImportProgressDrawer = () => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const [isMinimized, setIsMinimized] = useState(false);

  const isVisible = useAppStore((s) => s.isCsvUserImportDrawerVisible);
  const status = useAppStore((s) => s.csvUserImportStatus);
  const doneCount = useAppStore((s) => s.csvUserImportDoneCount);
  const total = useAppStore((s) => s.csvUserImportTotal);
  const items = useAppStore((s) => s.csvUserImportResults);
  const requestCancel = useAppStore((s) => s.requestCsvUserImportCancel);
  const dismiss = useAppStore((s) => s.dismissCsvUserImportDrawer);

  const isRunning = status === 'running';
  const isCancelled = status === 'cancelled';

  const itemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [prevRunningLine, setPrevRunningLine] = useState<number | null>(null);

  /**
   * Header counter mirrors upload: "Importing users X of Y" / "Imported X of Y".
   */
  const title: string = useMemo(() => {
    if (isCancelled) {
      return t('modal.csvUsersImportForm.drawerCancelled');
    }
    const counterKey = !isRunning
      ? 'modal.csvUsersImportForm.importedUsersCounter'
      : 'modal.csvUsersImportForm.importingUsersCounter';
    return t(counterKey, {
      amount: doneCount,
      total,
    });
  }, [doneCount, isCancelled, isRunning, t, total]);

  /**
   * Scroll to the user currently being created (first running item).
   */
  useEffect(() => {
    const running = items.find((item) => item.status === 'running');
    if (
      running &&
      itemRefs.current[String(running.lineNumber)] &&
      running.lineNumber !== prevRunningLine
    ) {
      itemRefs.current[String(running.lineNumber)]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      setPrevRunningLine(running.lineNumber);
    }
  }, [items, prevRunningLine]);

  /**
   * New import (total changes while drawer visible) → expand if minimized.
   */
  useEffect(() => {
    if (isMinimized) {
      setIsMinimized(false);
    }
  }, [total]);

  if (!isVisible) {
    return null;
  }

  const handleClose = (): void => {
    if (isRunning) {
      modal.confirm({
        title: t('modal.csvUsersImportForm.cancelConfirmTitle'),
        content: t('modal.csvUsersImportForm.cancelConfirmContent'),
        okText: t('modal.csvUsersImportForm.cancelImport'),
        cancelText: t('modal.csvUsersImportForm.continueImport'),
        onOk: () => requestCancel(),
      });
      return;
    }
    dismiss();
  };

  const toggleMinimized = (): void => {
    setIsMinimized((previous) => !previous);
  };

  const displayName = (item: CsvUserImportResultItem): string => {
    const name = [item.firstName, item.lastName].filter(Boolean).join(' ').trim();
    return name || item.email;
  };

  const avatarInitials = (item: CsvUserImportResultItem): ReactNode => {
    const first = item.firstName?.charAt(0)?.toUpperCase() ?? '';
    const last = item.lastName?.charAt(0)?.toUpperCase() ?? '';
    if (first || last) {
      return `${first}${last}`;
    }
    return <Icon name="AtSignIcon" />;
  };

  const avatarBackground = (item: CsvUserImportResultItem): string => {
    if (item.userId != null) {
      return getAvatarColor(item.userId);
    }
    return PENDING_AVATAR_BG;
  };

  return (
    <div
      id="user-import-progress-drawer"
      role="region"
      aria-label={t('modal.csvUsersImportForm.drawerAria')}
      className={clsx(
        'border-gray-medium w-full rounded-md border bg-white transition-all duration-200 md:w-[400px] lg:w-[500px]',
        isMinimized ? 'h-12' : 'h-80',
      )}
    >
      {/* Header bar for title, minimize, and clear/cancel buttons */}
      <div
        className={clsx(
          'bg-gray-light flex items-center justify-between rounded-t-md border-b px-3 py-2',
          isMinimized ? 'border-transparent' : 'border-gray-medium',
        )}
      >
        <div className="text-blue-dark text-base font-medium">{title}</div>
        <div className="flex items-center">
          <Button
            type="text"
            size="small"
            className="flex items-center pl-0"
            aria-label={
              isMinimized
                ? t('modal.csvUsersImportForm.expandDrawer')
                : t('modal.csvUsersImportForm.minimizeDrawer')
            }
            onClick={(clickEvent) => {
              clickEvent.stopPropagation();
              toggleMinimized();
            }}
            icon={
              <Icon
                name="ChevronDownIcon"
                className={clsx(
                  'mt-1 h-5 w-5 transform transition-transform duration-200',
                  isMinimized ? 'rotate-180' : '',
                )}
              />
            }
          />
          <Tooltip
            title={isRunning ? t('modal.csvUsersImportForm.cancelImport') : t('common.close')}
          >
            <Button
              type="text"
              size="small"
              className="flex items-center pl-0"
              aria-label={t('common.close')}
              onClick={(clickEvent) => {
                clickEvent.stopPropagation();
                handleClose();
              }}
              icon={<Icon name="XIcon" className="mt-1 h-4 w-4" />}
            />
          </Tooltip>
        </div>
      </div>

      {/* Flat list of user items — same row shell as UploadProgressDrawer */}
      <div className="divide-gray flex h-full max-h-fit flex-col divide-y overflow-y-auto px-3 pb-14">
        {isRunning && (
          <div className="bg-yellow-light relative flex min-h-[44px] items-center justify-between py-1.5 transition-all duration-200">
            <span className="text-yellow-dark text-xs">
              {t('modal.csvUsersImportForm.browserStayOpenWarning')}
            </span>
          </div>
        )}

        {items.map((item) => {
          const key = String(item.lineNumber);
          const label = displayName(item);
          const isFailed = item.status === 'failed';
          const isCanceled = item.status === 'cancelled';
          const showProgressBar = ['pending', 'running', 'failed', 'cancelled'].includes(
            item.status,
          );

          return (
            <div
              key={key}
              ref={(el) => {
                if (el) itemRefs.current[key] = el;
              }}
              className="relative flex min-h-[44px] items-center justify-between gap-2 py-1.5 transition-all duration-200"
            >
              <div className="flex min-w-0 items-center gap-2">
                <Avatar
                  size={28}
                  className="text-blue-dark! shrink-0! text-xs!"
                  style={{ backgroundColor: avatarBackground(item) }}
                  aria-hidden
                >
                  {avatarInitials(item)}
                </Avatar>
                <div className="flex min-w-0 flex-col truncate">
                  <span
                    className={clsx(
                      'max-w-60 truncate text-sm font-medium',
                      isFailed || isCanceled ? 'text-red-dark' : 'text-blue-dark',
                    )}
                  >
                    <TruncatedTooltip>{label}</TruncatedTooltip>
                  </span>
                  {label !== item.email && (
                    <span className="text-gray-dark max-w-60 truncate text-xs">{item.email}</span>
                  )}
                  {isFailed && item.message ? (
                    <span className="text-red-dark max-w-72 truncate text-xs">{item.message}</span>
                  ) : null}
                </div>
              </div>

              <div className="flex shrink-0 items-center justify-center">
                {((): ReactNode => {
                  switch (item.status) {
                    case 'pending':
                      return (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-dark text-xs">
                            {t('modal.csvUsersImportForm.waitingForImport')}
                          </span>
                          <Spin size="small" />
                        </div>
                      );
                    case 'running':
                      return (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-dark text-xs">
                            {t('modal.csvUsersImportForm.importingRow')}
                          </span>
                          <Spin size="small" />
                          <Tooltip title={t('modal.csvUsersImportForm.cancelImport')}>
                            <Button
                              type="text"
                              size="small"
                              icon={<Icon name="XIcon" className="h-4 w-4" />}
                              onClick={() => requestCancel()}
                              aria-label={t('modal.csvUsersImportForm.cancelImport')}
                            />
                          </Tooltip>
                        </div>
                      );
                    case 'created':
                      return <Icon name="CircleCheckIcon" className="text-green-dark h-5 w-5" />;
                    case 'skippedAlreadyExists':
                      return (
                        <div className="flex flex-row items-center gap-1">
                          <span className="text-gray-dark text-xs">
                            {t('modal.csvUsersImportForm.alreadyExists')}
                          </span>
                          <Icon name="CircleCheckIcon" className="text-green-dark h-5 w-5" />
                        </div>
                      );
                    case 'cancelled':
                      return (
                        <div className="flex flex-row items-center gap-1">
                          <Icon name="CircleSlashIcon" className="text-red-dark h-5 w-5" />
                          <span className="text-red-dark text-sm">{t('common.canceled')}</span>
                        </div>
                      );
                    case 'failed':
                      return (
                        <div className="flex flex-row items-center gap-1">
                          <Icon name="CircleXIcon" className="text-red-dark h-5 w-5" />
                          <span className="text-red-dark text-sm">{t('common.failed')}</span>
                        </div>
                      );
                    default:
                      return 'N/D';
                  }
                })()}
              </div>

              {showProgressBar && (
                <Progress
                  status={
                    item.status === 'failed' || item.status === 'cancelled' ? 'exception' : 'active'
                  }
                  className="absolute! right-0! bottom-0! left-0! z-10!"
                  size={{ height: 1 }}
                  percent={item.status === 'running' ? 50 : 0}
                  showInfo={false}
                  style={{ height: '1px', minHeight: '1px' }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UserImportProgressDrawer;
